import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertCircle, AlertTriangle, CheckCircle, InfoCircle, } from "@untitled-ui/icons-react";
import { PiInfo, PiX } from "react-icons/pi";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { tv } from "tailwind-variants";
import { match } from "ts-pattern";
import { cn } from "../cn";
export const Toaster = () => {
    return (_jsx(Sonner, { className: "group ", position: "top-right", toastOptions: {
            classNames: {
                toast: "w-full",
            },
        } }));
};
export function toast({ kind, title, description, actions, autoHide = false, }) {
    return sonnerToast.custom((id) => {
        return (_jsx("div", { className: "relative rounded-md shadow-lg", children: _jsx(Toast, { kind: kind, title: title, description: description, actions: actions?.({ dismiss: () => sonnerToast.dismiss(id) }), onClose: () => sonnerToast.dismiss(id) }) }));
    }, { duration: autoHide ? 5000 : Number.POSITIVE_INFINITY });
}
function copyToClipboard(content) {
    return navigator.clipboard.writeText(content);
}
export const clickToCopy = async (e, copyText) => {
    e.preventDefault();
    e.stopPropagation();
    await copyToClipboard(copyText);
    toast({
        description: `${copyText} copied to clipboard`,
        kind: "info",
        autoHide: true,
    });
    return;
};
export const dismissToast = sonnerToast.dismiss;
// todo: [standardise-colours]
export const toastVariants = tv({
    slots: {
        root: "rounded-md",
        title: "text-sm font-medium leading-normal",
        icon: "size-5",
    },
    variants: {
        kind: {
            default: {
                icon: "text-icon-primary",
                title: "text-primary",
                root: "bg-secondary",
            },
            info: {
                icon: "text-icon-info",
                title: "text-info",
                root: "bg-accent-info",
            },
            warning: {
                icon: "text-icon-warning",
                title: "text-warning",
                root: "bg-accent-warning",
            },
            error: {
                icon: "text-icon-danger",
                title: "text-danger",
                root: "bg-accent-danger",
            },
            success: {
                icon: "text-icon-success",
                title: "text-success",
                root: "bg-accent-success",
            },
        },
    },
    defaultVariants: { kind: "default" },
});
export function Toast({ title, description, actions, kind = "default", onClose, className, }) {
    const classes = toastVariants({ kind });
    const Icon = match(kind ?? "default")
        .with("info", () => PiInfo)
        .with("warning", () => AlertTriangle)
        .with("error", () => AlertCircle)
        .with("success", () => CheckCircle)
        .with("default", () => InfoCircle)
        .exhaustive();
    return (_jsxs("div", { className: cn(className, classes.root({
            className: "pointer-events-auto grid grid-flow-row grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 px-3 py-4",
        })), children: [_jsx("div", { className: "pt-px", children: _jsx(Icon, { className: classes.icon() }) }), title ? (_jsx("div", { className: classes.title({ className: "-mt-[1px] col-start-2" }), children: title })) : null, description ? (_jsx("div", { className: "col-start-2 text-primary text-sm leading-normal", children: description })) : null, actions ? _jsx("div", { className: "col-start-2 pt-2", children: actions }) : null, onClose ? (_jsx(PiX, { size: 16, className: "cursor-pointer", onClick: onClose })) : null] }));
}
/**
 * Use this for a consistent layout when adding an actions row on the bottom of a toast.
 *
 * Example:
 *
 * ```tsx
 * <Toast
 *    title="Information"
 *    description="Something"
 *    actions={
 *      <ToastActions>
 *        <Button>Dismiss</Button>
 *      </ToastActions>
 *    }
 *  />
 *
 * ```
 */
export function ToastActions(props) {
    return _jsx("div", { className: "flex justify-start gap-3", children: props.children });
}
